import { Overlay } from "@components/Overlay";
import { OverlayProps } from "@components/Overlay/types";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Flow, Routes, ShareGoalInviteAction, ShareGoalInviteStatus } from "@ctypes";
import {
  useActionShareGoalInvite,
  useErrorBanner,
  useGoalFlow,
  useModal,
  useNavigateToStep,
  useReceivedSharedGoalInvites,
} from "@hooks";
import { i18n, Translations } from "@i18n";
import { SharedGoalItem } from "./SharedGoalItem";

export const AcceptSharedGoalModal = () => {
  const { dismiss } = useModal();
  const { showErrorBanner } = useErrorBanner();
  const navigate = useNavigateToStep();

  const { resetGoalProps } = useGoalFlow();

  const { data, loading, error } = useReceivedSharedGoalInvites();
  const [actionShareGoalInvite, { loading: isUpdatingInvite }] = useActionShareGoalInvite();

  const pendingInvite = data?.find((invite) => invite?.status === ShareGoalInviteStatus.Pending);

  if (loading || error || !pendingInvite) {
    return null;
  }

  const handleAccept = () => {
    resetGoalProps({
      flow: Flow.AcceptSharedGoal,
      pendingInvite,
    });
    dismiss();
    navigate(Routes.AccountType);
  };

  const handleReject = async () => {
    try {
      await actionShareGoalInvite({
        variables: {
          action: ShareGoalInviteAction.Reject,
          shareGoalInviteId: pendingInvite.id!,
        },
      });
    } catch {
      showErrorBanner();
    } finally {
      dismiss();
    }
  };

  const props: OverlayProps = {
    title: i18n.t(Translations.AcceptSharedGoalModalTitle),
    cta: {
      primary: {
        title: i18n.t(Translations.AcceptSharedGoalModalAcceptCTA),
        onClick: handleAccept,
      },
      secondary: {
        title: i18n.t(Translations.AcceptSharedGoalModalRejectCTA),
        onClick: handleReject,
        loading: isUpdatingInvite,
      },
    },
    horizontal: true,
  };

  return (
    <Overlay {...props}>
      <SharedGoalItem
        goalName={pendingInvite.goalName}
        goalType={pendingInvite.goalType}
        firstName={pendingInvite.inviterFirstName}
        lastName={pendingInvite.inviterLastName}
      />
      <Spacing size={SpacingSizes.M} />
    </Overlay>
  );
};
